// src/data/claimNext.js
//
// BOB, 2026-10-25: "Membership should see easily where they are and whats expected of them, and how
// to complete the claim and what to do when it is complete. Do you really think that exisits in its
// current form" - and the honest answer was no. The step-by-step how-to exists (claimHowTo.js) and is
// mounted on Quick File, collapsed; the full New Claim wizard never shows it; and the screen a member
// reaches AFTER filing says only that the claim was "submitted and saved to the platform database",
// which is a developer's sentence and tells him nothing about what now happens to his claim.
//
// This module answers the last of his four questions - WHAT TO DO WHEN IT IS COMPLETE - and it adds
// no fact of its own. Every line is projected from the agreement's own recorded requirements, the same
// source the how-to steps and the deadline panel read, and where nothing is on record it says so in
// words rather than filling the gap (PD-4).
//
// Pure. Proved by scripts\test-claim-next.mjs.

import { getClaimRequirements } from './claimRequirements';
import { period } from './claimHowTo';

export const NOTHING_ON_RECORD =
  'This platform holds no claim-handling provisions for your agreement yet, so it cannot tell you what '
  + 'the next step is. Your Local Chairperson can - ask him, and keep your own copy of what you filed.';

// Said on every screen that rests on the agreement (ruling 324): the words were read from the
// agreement but no General Chairperson has yet signed them off.
export const UNCONFIRMED =
  'These steps were read from your agreement and no General Chairperson has confirmed them yet.';

const cite = (c, fallback) => (c && c.cite) ? c.cite : (fallback || '');
const clockOf = (req, which) =>
  (Array.isArray(req.clocks) ? req.clocks : []).find((c) => c && String(c.label || '').indexOf(which) === 0
    || (c && c.key === which)) || null;

// What happens now, in the order it happens. Each item: { n, title, body[], cite }
export function whatHappensNow(agreementName, opts) {
  const o = opts || {};
  const req = getClaimRequirements(agreementName);
  if (!req) return { recorded: false, text: NOTHING_ON_RECORD, steps: [] };

  const steps = [];
  const add = (title, body, c) => steps.push({ n: steps.length + 1, title, body: body.filter(Boolean), cite: c || '' });

  // 1. Who has it now. The officers are the platform's own routing, not the agreement's - said so.
  add('Your Local and General Chairperson have it',
    ['The claim went to them when you filed it. They are the ones who carry it from here, and they are '
      + 'the ones to ask about it.',
     o.committee ? 'Committee of record on the claim: ' + o.committee + '.' : '',
     'That routing is how this platform works; it is not a term of your agreement.'],
    '');

  // 2. Delivery to the carrier - the agreement's own rule where it is recorded.
  const presentment = req.presentmentOfficer || req.presentTo || '';
  add('It has to reach the carrier',
    [presentment
      ? 'Under your agreement the claim is presented to ' + presentment + '.'
      : 'Your agreement’s named carrier officer is not on this platform yet, so the platform cannot '
        + 'tell you who receives it. Your General Chairperson holds that.',
     'Keep proof of how and when it was delivered - a hand receipt, a certified-mail receipt, or the fax '
       + 'confirmation. An e-mailed receipt can be challenged.'],
    cite(null, req.claimsRule || req.source));

  // 3. The carrier's clock, from the agreement.
  const carrier = (Array.isArray(req.clocks) ? req.clocks : []).find((c) => c && c.party === 'carrier');
  add('The carrier has a time limit to answer',
    [carrier
      ? 'Your agreement gives the carrier ' + period(carrier) + ' to answer' + (carrier.from ? ' from ' + carrier.from : '') + '.'
      : 'No carrier answer period is recorded on this platform for your agreement.',
     carrier && carrier.onSilence
      ? 'If the carrier does not answer in time, the agreement says: “' + carrier.onSilence + '”'
      : (carrier ? 'What happens if the carrier does not answer in time is not recorded here.' : ''),
     'The platform shows that date on your claim, and your officers see the same clock on their desks.'],
    cite(carrier, req.claimsRule || req.source));

  // 4. If it is denied.
  const appeal = (Array.isArray(req.clocks) ? req.clocks : []).find((c) => c && c.party === 'organization');
  add('If it is denied',
    [appeal
      ? 'The Organization has ' + period(appeal) + ' to appeal or list it for conference' + (appeal.from ? ' from ' + appeal.from : '') + '. '
        + 'That is your General Chairperson’s act, not yours.'
      : 'No appeal period is recorded on this platform for your agreement; your General Chairperson holds that.',
     'You do not have to do anything to start it, and you are told when it moves.'],
    cite(appeal, req.claimsRule || req.source));

  // 5. What the member keeps and does.
  add('What to keep, and what to do',
    ['Keep your copy of the claim as filed and your proof of delivery.',
     'Nothing more is needed from you unless your Local Chairperson asks - usually for a document, a '
       + 'date, or a name.',
     'If you hear nothing at all, ask your Local Chairperson. A claim that is sitting is still your claim.'],
    '');

  return { recorded: true, steps, unconfirmed: UNCONFIRMED, agreement: agreementName, claimsRule: req.claimsRule || req.source || '' };
}

// One line for the top of the filed screen: what the member should take away in a sentence.
export function filedHeadline(agreementName, claimId) {
  const id = claimId ? ' ' + claimId : '';
  const req = getClaimRequirements(agreementName);
  const carrier = req && (Array.isArray(req.clocks) ? req.clocks : []).find((c) => c && c.party === 'carrier');
  if (!req) {
    return 'Claim' + id + ' is filed and your Local and General Chairperson have it. This platform does not '
      + 'hold your agreement’s time limits, so it cannot tell you when an answer is due.';
  }
  return 'Claim' + id + ' is filed. Your Local and General Chairperson have it'
    + (carrier ? ', and the carrier has ' + period(carrier) + ' to answer once it is presented' : '')
    + '. Keep your copy and your proof of delivery.';
}
